import { getAuth } from 'firebase/auth';
import {
  deleteDoc,
  deleteField,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc
} from 'firebase/firestore';
import { v4 as uuid } from 'uuid';
import {
  userPlaylistDocRef,
  userPlaylistItemDocRef,
  userPlaylistItemsCollRef,
  userPlaylistsCollRef,
  userProjectDocRef,
  userVideoDocRef
} from '../utils';
import {
  getLibraryProjectCollection,
  getLibraryProjectMembershipKey,
  libraryProjectMatchesMembership,
  libraryProjectToPlaylistReference,
  listLibraryProjects
} from './videoLibrary';
import type {
  LibraryItem,
  LibraryItemCollection,
  LibraryPlaylist,
  LibraryPlaylistMembership,
  NormalizedLibraryProject
} from '../types';

export const normalizePlaylistTitle = (value: string): string => {
  return value.trim().replace(/\s+/g, ' ');
};

const normalizePlaylistId = (value: string): string => value.trim();

const isLibraryItemCollection = (value: unknown): value is LibraryItemCollection => (
  value === 'projects' || value === 'videos'
);

const normalizePosition = (value: unknown): number | undefined => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return undefined;
  return value;
};

const membershipIdFor = (item: Pick<LibraryItem, 'id' | '_collection'>): string =>
  `${item._collection}:${item.id}`;

export const playlistMembershipItemKey = (
  membership: Pick<LibraryPlaylistMembership, 'itemCollection' | 'itemId'>
): string => `${membership.itemCollection}:${membership.itemId}`;

export const createLibraryProjectPlaylistMembership = (
  playlistId: string,
  project: NormalizedLibraryProject,
  position: number,
  options: { legacy?: boolean } = {}
): LibraryPlaylistMembership => ({
  id: getLibraryProjectMembershipKey(project),
  playlistId,
  itemId: project.id,
  itemCollection: getLibraryProjectCollection(project),
  position,
  addedAt: project.createdAt,
  ...(options.legacy ? { legacy: true } : {})
});

export const playlistMembershipMatchesLibraryProject = (
  membership: Pick<LibraryPlaylistMembership, 'itemCollection' | 'itemId'>,
  project: NormalizedLibraryProject
): boolean => libraryProjectMatchesMembership(project, membership);

const itemRefFor = (
  uid: string,
  item: Pick<LibraryItem, 'id' | '_collection'>
) => item._collection === 'projects'
  ? userProjectDocRef(uid, item.id)
  : userVideoDocRef(uid, item.id);

const assertPlaylistId = (playlistId: string): string => {
  const normalized = normalizePlaylistId(playlistId);
  if (!normalized) throw new Error('Playlist id is required');
  return normalized;
};

const assertLibraryItem = (
  item: Pick<LibraryItem, 'id' | '_collection'>
): Pick<LibraryItem, 'id' | '_collection'> => {
  if (!item || typeof item.id !== 'string' || !item.id.trim() || !isLibraryItemCollection(item._collection)) {
    throw new Error('A valid library item is required');
  }
  return { id: item.id.trim(), _collection: item._collection };
};

const compareMemberships = (
  first: LibraryPlaylistMembership,
  second: LibraryPlaylistMembership
): number => {
  const positionDifference = first.position - second.position;
  return positionDifference || first.id.localeCompare(second.id);
};

const normalizeMembership = (
  playlistId: string,
  id: string,
  data: Record<string, unknown>
): LibraryPlaylistMembership | null => {
  if (typeof data.itemId !== 'string' || !data.itemId.trim() || !isLibraryItemCollection(data.itemCollection)) {
    return null;
  }

  return {
    id,
    playlistId,
    itemId: data.itemId.trim(),
    itemCollection: data.itemCollection,
    position: normalizePosition(data.position) ?? Number.MAX_SAFE_INTEGER,
    addedAt: data.addedAt
  };
};

const listStoredPlaylistMemberships = async (
  uid: string,
  playlistId: string
): Promise<LibraryPlaylistMembership[]> => {
  const snap = await getDocs(userPlaylistItemsCollRef(uid, playlistId));
  return snap.docs
    .map(d => normalizeMembership(playlistId, d.id, d.data() as Record<string, unknown>))
    .filter((membership): membership is LibraryPlaylistMembership => Boolean(membership))
    .sort(compareMemberships);
};

const getNextPosition = (memberships: LibraryPlaylistMembership[]): number => {
  const positions = memberships
    .map(membership => membership.position)
    .filter(position => Number.isFinite(position) && position !== Number.MAX_SAFE_INTEGER);
  return positions.length > 0 ? Math.max(...positions) + 1 : 0;
};

const normalizePlaylistIds = (playlistIds: string[]): string[] => (
  Array.from(new Set(playlistIds.map(normalizePlaylistId).filter(playlistId => playlistId.length > 0)))
);

const mergeLegacyPlaylistMemberships = (
  playlistId: string,
  storedMemberships: LibraryPlaylistMembership[],
  libraryProjects: NormalizedLibraryProject[]
): LibraryPlaylistMembership[] => {
  const storedKeys = new Set(
    storedMemberships.map(playlistMembershipItemKey)
  );
  let nextPosition = getNextPosition(storedMemberships);

  const legacyMemberships = libraryProjects
    .filter(project => project.playlistId === playlistId)
    .filter(project => {
      const key = getLibraryProjectMembershipKey(project);
      if (storedKeys.has(key)) return false;
      storedKeys.add(key);
      return true;
    })
    .map(project => createLibraryProjectPlaylistMembership(
      playlistId,
      project,
      nextPosition++,
      { legacy: true }
    ));

  return [...storedMemberships, ...legacyMemberships].sort(compareMemberships);
};

export async function listPlaylists(): Promise<LibraryPlaylist[]> {
  const { currentUser } = getAuth();
  if (!currentUser) return [];
  const uid = currentUser.uid;

  const snap = await getDocs(userPlaylistsCollRef(uid));
  return snap.docs
    .map(d => {
      const data = d.data() as any;
      return {
        id: d.id,
        title: normalizePlaylistTitle(String(data.title || 'Untitled playlist')),
        ownerId: data.ownerId || uid,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt
      };
    })
    .filter(playlist => playlist.title.length > 0)
    .sort((a, b) => a.title.localeCompare(b.title));
}

export async function createPlaylist(title: string): Promise<LibraryPlaylist> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');

  const cleanTitle = normalizePlaylistTitle(title);
  if (!cleanTitle) throw new Error('Playlist title is required');

  const normalizedTitleKey = cleanTitle.toLocaleLowerCase();
  const existingPlaylists = await listPlaylists();
  if (existingPlaylists.some(playlist => (
    playlist.title.toLocaleLowerCase() === normalizedTitleKey
  ))) {
    throw new Error('A playlist with this name already exists');
  }

  const id = uuid();
  const timestamp = serverTimestamp();
  const playlistData = {
    schemaVersion: 1,
    ownerId: user.uid,
    title: cleanTitle,
    createdAt: timestamp,
    updatedAt: timestamp
  };

  await setDoc(userPlaylistDocRef(user.uid, id), playlistData);

  return {
    id,
    title: cleanTitle,
    ownerId: user.uid,
    createdAt: timestamp,
    updatedAt: timestamp
  };
}

export async function renamePlaylist(
  playlistId: string,
  title: string
): Promise<LibraryPlaylist> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');

  const normalizedPlaylistId = assertPlaylistId(playlistId);
  const cleanTitle = normalizePlaylistTitle(title);
  if (!cleanTitle) throw new Error('Playlist title is required');

  const normalizedTitleKey = cleanTitle.toLocaleLowerCase();
  const existingPlaylists = await listPlaylists();
  if (existingPlaylists.some(playlist => (
    playlist.id !== normalizedPlaylistId &&
    playlist.title.toLocaleLowerCase() === normalizedTitleKey
  ))) {
    throw new Error('A playlist with this name already exists');
  }

  const existingPlaylist = existingPlaylists.find(playlist => playlist.id === normalizedPlaylistId);
  const timestamp = serverTimestamp();
  await updateDoc(userPlaylistDocRef(user.uid, normalizedPlaylistId), {
    title: cleanTitle,
    updatedAt: timestamp
  });

  return {
    id: normalizedPlaylistId,
    title: cleanTitle,
    ownerId: existingPlaylist?.ownerId || user.uid,
    createdAt: existingPlaylist?.createdAt,
    updatedAt: timestamp
  };
}

/**
 * Returns ordered playlist membership references. Legacy playlistId fields are
 * included after stored memberships so old data remains visible before it is
 * backfilled. The returned records never contain a copy of the video document.
 */
export async function listPlaylistMemberships(
  playlistId: string
): Promise<LibraryPlaylistMembership[]> {
  const { currentUser } = getAuth();
  if (!currentUser) return [];
  const normalizedPlaylistId = assertPlaylistId(playlistId);
  const membershipsByPlaylist = await listPlaylistMembershipsForPlaylists([normalizedPlaylistId]);
  return membershipsByPlaylist[normalizedPlaylistId] || [];
}

/**
 * Loads memberships for several already-known playlists in one batch. Each
 * playlist still needs one membership-subcollection read, but the library
 * collections are read only once total and legacy playlistId references are
 * merged across all requested playlists in memory.
 */
export async function listPlaylistMembershipsForPlaylists(
  playlistIds: string[]
): Promise<Record<string, LibraryPlaylistMembership[]>> {
  const normalizedPlaylistIds = normalizePlaylistIds(playlistIds);
  const membershipsByPlaylist: Record<string, LibraryPlaylistMembership[]> = {};
  normalizedPlaylistIds.forEach(playlistId => {
    membershipsByPlaylist[playlistId] = [];
  });

  const { currentUser } = getAuth();
  if (!currentUser || normalizedPlaylistIds.length === 0) return membershipsByPlaylist;

  const [storedMembershipGroups, libraryProjects] = await Promise.all([
    Promise.all(normalizedPlaylistIds.map(async playlistId => ({
      playlistId,
      memberships: await listStoredPlaylistMemberships(currentUser.uid, playlistId)
    }))),
    listLibraryProjects()
  ]);

  storedMembershipGroups.forEach(({ playlistId, memberships }) => {
    membershipsByPlaylist[playlistId] = mergeLegacyPlaylistMemberships(
      playlistId,
      memberships,
      libraryProjects
    );
  });

  return membershipsByPlaylist;
}

/**
 * Adds a project/video to a playlist without copying its metadata. Re-adding
 * the same item is idempotent and preserves its existing position.
 */
export async function addLibraryItemToPlaylist(
  playlistId: string,
  item: Pick<LibraryItem, 'id' | '_collection'>,
  position?: number
): Promise<LibraryPlaylistMembership> {
  const auth = getAuth();
  if (!auth.currentUser) throw new Error('Not authenticated');

  const normalizedPlaylistId = assertPlaylistId(playlistId);
  const normalizedItem = assertLibraryItem(item);
  const membershipId = membershipIdFor(normalizedItem);
  const membershipRef = userPlaylistItemDocRef(auth.currentUser.uid, normalizedPlaylistId, membershipId);
  const existing = await getDoc(membershipRef);
  const existingMembership = existing.exists()
    ? normalizeMembership(normalizedPlaylistId, membershipId, existing.data() as Record<string, unknown>)
    : null;
  const nextPosition = existingMembership?.position ?? normalizePosition(position) ?? getNextPosition(
    await listStoredPlaylistMemberships(auth.currentUser.uid, normalizedPlaylistId)
  );
  const timestamp = serverTimestamp();
  const data = {
    schemaVersion: 1,
    itemId: normalizedItem.id,
    itemCollection: normalizedItem._collection,
    position: nextPosition,
    addedAt: existingMembership?.addedAt || timestamp
  };

  await setDoc(membershipRef, data, { merge: true });

  return {
    id: membershipId,
    playlistId: normalizedPlaylistId,
    itemId: normalizedItem.id,
    itemCollection: normalizedItem._collection,
    position: nextPosition,
    addedAt: data.addedAt
  };
}

export async function addLibraryProjectToPlaylist(
  playlistId: string,
  project: NormalizedLibraryProject,
  position?: number
): Promise<LibraryPlaylistMembership> {
  return addLibraryItemToPlaylist(
    playlistId,
    libraryProjectToPlaylistReference(project),
    position
  );
}

/**
 * Persists a playlist's current membership order. The membership documents
 * remain the source of truth: library project/video documents are never
 * touched by this operation.
 *
 * Legacy playlistId records are synthesized by the read path. If a user
 * explicitly reorders one, the corresponding reference is materialized so its
 * chosen position can be preserved on the next refresh.
 */
export async function reorderPlaylistMemberships(
  playlistId: string,
  orderedMemberships: LibraryPlaylistMembership[]
): Promise<LibraryPlaylistMembership[]> {
  const auth = getAuth();
  if (!auth.currentUser) throw new Error('Not authenticated');

  const normalizedPlaylistId = assertPlaylistId(playlistId);
  const seenMembershipIds = new Set<string>();

  orderedMemberships.forEach(membership => {
    if (!membership || typeof membership.id !== 'string' || !membership.id.trim()) {
      throw new Error('A valid playlist membership is required');
    }
    if (membership.playlistId !== normalizedPlaylistId) {
      throw new Error('Playlist membership belongs to a different playlist');
    }
    if (seenMembershipIds.has(membership.id)) {
      throw new Error('Playlist memberships must be unique');
    }
    assertLibraryItem({ id: membership.itemId, _collection: membership.itemCollection });
    seenMembershipIds.add(membership.id);
  });

  const nextMemberships = orderedMemberships.map((membership, index) => ({
    ...membership,
    position: index
  }));
  const previousPositionsByMembershipId = new Map(
    orderedMemberships.map(membership => [membership.id, membership.position])
  );
  const changedMemberships = nextMemberships
    .map(membership => ({
      membership,
      previousPosition: previousPositionsByMembershipId.get(membership.id) ?? membership.position
    }))
    .filter(({ membership, previousPosition }) => membership.position !== previousPosition);

  const appliedMemberships: Array<{
    membership: LibraryPlaylistMembership;
    previousPosition: number;
  }> = [];

  try {
    for (const change of changedMemberships) {
      const membershipRef = userPlaylistItemDocRef(
        auth.currentUser.uid,
        normalizedPlaylistId,
        change.membership.id
      );

      if (change.membership.legacy) {
        await setDoc(membershipRef, {
          schemaVersion: 1,
          itemId: change.membership.itemId,
          itemCollection: change.membership.itemCollection,
          position: change.membership.position,
          addedAt: change.membership.addedAt || serverTimestamp()
        }, { merge: true });
      } else {
        await updateDoc(membershipRef, { position: change.membership.position });
      }
      appliedMemberships.push(change);
    }
  } catch (error) {
    // A failed write must not leave the UI or the already-written membership
    // documents in a partially reordered state. Best-effort rollback keeps
    // the original write error as the actionable failure for the caller.
    await Promise.all(appliedMemberships.map(async change => {
      const membershipRef = userPlaylistItemDocRef(
        auth.currentUser!.uid,
        normalizedPlaylistId,
        change.membership.id
      );
      try {
        if (change.membership.legacy) {
          await deleteDoc(membershipRef);
        } else {
          await updateDoc(membershipRef, { position: change.previousPosition });
        }
      } catch (rollbackError) {
        console.warn('Failed to roll back playlist order write:', rollbackError);
      }
    }));
    throw error;
  }

  return nextMemberships;
}

/** Deletes a playlist container and its stored membership references only. */
export async function deletePlaylist(playlistId: string): Promise<void> {
  const auth = getAuth();
  if (!auth.currentUser) throw new Error('Not authenticated');

  const normalizedPlaylistId = assertPlaylistId(playlistId);
  const uid = auth.currentUser.uid;
  const memberships = await listStoredPlaylistMemberships(uid, normalizedPlaylistId);

  for (const membership of memberships) {
    await deleteDoc(userPlaylistItemDocRef(uid, normalizedPlaylistId, membership.id));
  }

  await deleteDoc(userPlaylistDocRef(uid, normalizedPlaylistId));
}

/** Removes only the playlist membership; the library item is never deleted. */
export async function removeLibraryItemFromPlaylist(
  playlistId: string,
  item: Pick<LibraryItem, 'id' | '_collection'>
): Promise<void> {
  const auth = getAuth();
  if (!auth.currentUser) throw new Error('Not authenticated');

  const normalizedPlaylistId = assertPlaylistId(playlistId);
  const normalizedItem = assertLibraryItem(item);
  const uid = auth.currentUser.uid;
  await deleteDoc(userPlaylistItemDocRef(uid, normalizedPlaylistId, membershipIdFor(normalizedItem)));

  // Clear the old one-playlist field only when it points at this playlist. This
  // keeps removal correct for legacy records without touching unrelated data.
  const itemRef = itemRefFor(uid, normalizedItem);
  const itemSnap = await getDoc(itemRef);
  if (itemSnap.exists() && (itemSnap.data() as Record<string, unknown>).playlistId === normalizedPlaylistId) {
    await updateDoc(itemRef, { playlistId: deleteField(), updatedAt: serverTimestamp() });
  }
}

export async function removeLibraryProjectFromPlaylist(
  playlistId: string,
  project: NormalizedLibraryProject
): Promise<void> {
  await removeLibraryItemFromPlaylist(
    playlistId,
    libraryProjectToPlaylistReference(project)
  );
}

/** Remove every playlist membership for an item before its library record is deleted. */
export async function removeLibraryItemFromAllPlaylists(
  item: Pick<LibraryItem, 'id' | '_collection'>
): Promise<void> {
  const auth = getAuth();
  if (!auth.currentUser) throw new Error('Not authenticated');

  const normalizedItem = assertLibraryItem(item);

  const playlists = await listPlaylists();
  if (playlists.length === 0) return;

  // Deletion cleanup only needs stored relationship documents. Avoid the
  // legacy-compatibility library read here: the library item is about to be
  // deleted, and a permission failure in either library collection should not
  // prevent cleanup of the membership documents we can access.
  const membershipsByPlaylist = await Promise.all(playlists.map(async playlist => ({
    playlistId: playlist.id,
    memberships: await listStoredPlaylistMemberships(auth.currentUser!.uid, playlist.id)
  })));
  const membershipDeletes = membershipsByPlaylist.flatMap(({ playlistId, memberships }) => (
    memberships
      .filter(membership => (
        membership.itemId === normalizedItem.id && membership.itemCollection === normalizedItem._collection
      ))
      .map(membership => deleteDoc(
        userPlaylistItemDocRef(auth.currentUser!.uid, playlistId, membership.id)
      ))
  ));

  await Promise.all(membershipDeletes);
}

export async function removeLibraryProjectFromAllPlaylists(
  project: NormalizedLibraryProject
): Promise<void> {
  await removeLibraryItemFromAllPlaylists(libraryProjectToPlaylistReference(project));
}

/**
 * Copies legacy playlistId references into membership documents. It is safe to
 * run repeatedly: existing collection/item pairs are skipped and legacy fields
 * are intentionally retained until all callers have migrated.
 */
export async function backfillLegacyPlaylistMemberships(): Promise<number> {
  const auth = getAuth();
  if (!auth.currentUser) return 0;
  const uid = auth.currentUser.uid;
  const legacyItems = (await listLibraryProjects()).filter(project => Boolean(project.playlistId));
  const grouped = new Map<string, NormalizedLibraryProject[]>();

  legacyItems.forEach(project => {
    const playlistId = project.playlistId as string;
    const group = grouped.get(playlistId) || [];
    group.push(project);
    grouped.set(playlistId, group);
  });

  let migratedCount = 0;
  for (const [playlistId, items] of Array.from(grouped.entries())) {
    const existing = await listStoredPlaylistMemberships(uid, playlistId);
    const existingKeys = new Set(
      existing.map(membership => `${membership.itemCollection}:${membership.itemId}`)
    );
    let nextPosition = getNextPosition(existing);

    for (const item of items) {
      const key = getLibraryProjectMembershipKey(item);
      if (existingKeys.has(key)) continue;

      const membership = {
        schemaVersion: 1,
        itemId: item.id,
        itemCollection: getLibraryProjectCollection(item),
        position: nextPosition++,
        addedAt: serverTimestamp()
      };
      await setDoc(userPlaylistItemDocRef(uid, playlistId, key), membership, { merge: true });
      existingKeys.add(key);
      migratedCount += 1;
    }
  }

  return migratedCount;
}
